/**
 * Benutzerrollen und Berechtigungen.
 *
 * Berechtigungen sind Strings der Form "bereich:aktion". Rollen buendeln
 * Berechtigungen; ein Benutzer hat genau eine Rolle. Das reicht fuer einen
 * kleinen Betrieb und laesst sich spaeter auf mehrere Rollen je Benutzer
 * erweitern, ohne die Pruefstellen im Code anzufassen.
 */
export const ROLE_LABELS = {
    admin: 'Administration',
    accounting: 'Buchhaltung',
    sales: 'Vertrieb',
    purchasing: 'Einkauf',
    warehouse: 'Lager',
    viewer: 'Nur Lesen',
};
/**
 * Alle Rechte mit Bezeichnung, gegliedert nach Bereich.
 *
 * Diese Liste ist die Grundlage der Rechtematrix in der Oberflaeche. Ein
 * neues Recht muss hier eingetragen werden, sonst laesst es sich nicht
 * vergeben - das ist Absicht: Ein Recht, das niemand vergeben kann, waere
 * ein totes Recht im Code.
 *
 * Ein Bereich, der zu einem Fachmodul gehoert, traegt dessen Schluessel in
 * `module`. Bei abgeschaltetem Modul erscheint der Bereich weder in der
 * Rechtematrix noch laesst sich eines seiner Rechte vergeben - siehe
 * `permissionSectionsFor` und `grantablePermissions`.
 */
export const PERMISSION_SECTIONS = [
    {
        title: 'Stammdaten',
        permissions: [
            {
                key: 'masterdata:read',
                label: 'Ansehen',
                description: 'Kunden, Lieferanten, Artikel und Warengruppen einsehen',
            },
            {
                key: 'masterdata:write',
                label: 'Bearbeiten',
                description: 'Stammdaten anlegen, ändern und deaktivieren',
            },
            {
                key: 'masterdata:lock',
                label: 'Sperren',
                description: 'Kunden und Lieferanten sperren und entsperren (Satzsperre). Gesperrte Datensätze darf nur ändern, wer dieses Recht hat; Belege für gesperrte Partner kann niemand anlegen oder festschreiben.',
            },
        ],
    },
    {
        title: 'Verkauf',
        permissions: [
            { key: 'sales:read', label: 'Ansehen', description: 'Angebote bis Rechnungen einsehen' },
            { key: 'sales:write', label: 'Bearbeiten', description: 'Verkaufsbelege erfassen und ändern' },
            {
                key: 'sales:post',
                label: 'Festschreiben',
                description: 'Belegnummer vergeben, Beleg unveränderlich machen',
            },
            {
                key: 'sales:cancel',
                label: 'Stornieren',
                description: 'Festgeschriebene Verkaufsbelege stornieren',
            },
            {
                key: 'sales:margin',
                label: 'Rohertrag sehen',
                description: 'Einstandspreis und Rohertrag je Position und Beleg sowie am Artikel einsehen',
            },
        ],
    },
    {
        title: 'Einkauf',
        permissions: [
            { key: 'purchase:read', label: 'Ansehen', description: 'Bestellungen und Eingangsrechnungen einsehen' },
            { key: 'purchase:write', label: 'Bearbeiten', description: 'Einkaufsbelege erfassen und ändern' },
            { key: 'purchase:post', label: 'Festschreiben', description: 'Einkaufsbelege festschreiben' },
            { key: 'purchase:cancel', label: 'Stornieren', description: 'Einkaufsbelege stornieren' },
        ],
    },
    {
        title: 'Lager',
        permissions: [
            { key: 'stock:read', label: 'Ansehen', description: 'Bestände und Bewegungen einsehen' },
            {
                key: 'stock:write',
                label: 'Buchen',
                description: 'Inventur, Korrekturen und Umlagerungen buchen',
            },
        ],
    },
    {
        title: 'Buchhaltung',
        permissions: [
            { key: 'accounting:read', label: 'Ansehen', description: 'Journal, offene Posten und Auswertungen' },
            { key: 'accounting:write', label: 'Buchen', description: 'Zahlungen erfassen und zuordnen' },
            { key: 'accounting:export', label: 'Exportieren', description: 'DATEV-Buchungsstapel erzeugen' },
            {
                key: 'accounting:open-items',
                label: 'Offene Posten je Partner',
                description: 'Offene Posten und Kreditlimit in der Partnerakte einsehen - ohne Journal und Zahlungen. Für Außendienst gedacht; in „Ansehen" bereits enthalten',
            },
        ],
    },
    {
        title: 'Auswertungen',
        permissions: [
            {
                key: 'reports:read',
                label: 'Berichte ansehen',
                description: 'Auswertungslisten mit Umsätzen je Mitarbeiter, Kunde und Artikel sowie offenen Posten',
            },
        ],
    },
    {
        title: 'CRM',
        module: 'crm',
        permissions: [
            {
                key: 'crm:read',
                label: 'Ansehen',
                description: 'Kontakthistorie und Wiedervorlagen einsehen',
            },
            {
                key: 'crm:write',
                label: 'Bearbeiten',
                description: 'Aktivitäten erfassen und erledigen - schließt Wiedervorlagen für andere Benutzer ein',
            },
        ],
    },
    {
        title: 'Reklamationen',
        module: 'complaints',
        permissions: [
            {
                key: 'complaints:read',
                label: 'Ansehen',
                description: 'Reklamationsvorgänge einsehen',
            },
            {
                key: 'complaints:write',
                label: 'Bearbeiten',
                description: 'Reklamationen erfassen, bearbeiten, abschließen und Rückläufer buchen',
            },
        ],
    },
    {
        title: 'Reparaturen',
        module: 'repairs',
        permissions: [
            {
                key: 'repairs:read',
                label: 'Ansehen',
                description: 'Reparaturaufträge einsehen',
            },
            {
                key: 'repairs:write',
                label: 'Bearbeiten',
                description: 'Reparaturen annehmen, Positionen erfassen, Status ändern - schließt das Anlegen von Kostenvoranschlags- und Rechnungsentwürfen ein',
            },
        ],
    },
    {
        // Canalis ist die Benutzerquelle fuer die angeschlossenen Anwendungen:
        // Wer hier "Zugriff" hat, kann sich dort mit dem Canalis-Passwort
        // anmelden; "Verwalten" macht ihn dort zum Verwalter.
        title: 'Anwendungen',
        permissions: [
            { key: 'portal:access', label: 'Außendienst-Portal', description: 'Anmeldung am Außendienst-Portal' },
            { key: 'portal:manage', label: 'Portal verwalten', description: 'Verwalterrechte im Außendienst-Portal' },
            { key: 'wissen:access', label: 'Wissensdatenbank', description: 'Anmeldung an der Wissensdatenbank' },
            { key: 'wissen:manage', label: 'Wissen verwalten', description: 'Verwalterrechte in der Wissensdatenbank' },
            { key: 'zeit:access', label: 'Zeit-App', description: 'Anmeldung an der Zeiterfassung' },
            { key: 'zeit:manage', label: 'Zeit verwalten', description: 'Zeiten aller Mitarbeiter einsehen und korrigieren' },
        ],
    },
    {
        title: 'Verwaltung',
        permissions: [
            { key: 'settings:read', label: 'Einstellungen ansehen', description: 'Firmendaten und Vorgaben einsehen' },
            {
                key: 'settings:write',
                label: 'Einstellungen ändern',
                description: 'Firmendaten, Nummernkreise, Warengruppen, Lagerorte, Belegvorlagen',
            },
            {
                key: 'users:manage',
                label: 'Benutzer verwalten',
                description: 'Benutzer, Gruppen und Rechte - schließt die Rechtevergabe ein',
            },
            { key: 'audit:read', label: 'Protokoll ansehen', description: 'Änderungsprotokoll einsehen' },
        ],
    },
];
/** Alle Rechte als flache Liste, in der Reihenfolge der Gliederung. */
export const ALL_PERMISSIONS = PERMISSION_SECTIONS.flatMap((section) => section.permissions.map((entry) => entry.key));
/** Die Rechtebereiche, die diese Installation betreffen. */
export function permissionSectionsFor(modules) {
    return PERMISSION_SECTIONS.filter((section) => !section.module || modules.includes(section.module));
}
/**
 * Die Rechte, die sich in dieser Installation vergeben lassen.
 *
 * Rechte abgeschalteter Module fehlen bewusst: Ein Recht, dessen Modul es
 * nicht gibt, waere fuer die Verwaltung nur verwirrend. Bereits in der
 * Datenbank stehende Eintraege bleiben unberuehrt - schaltet jemand das
 * Modul wieder ein, gelten die alten Zuordnungen weiter.
 */
export function grantablePermissions(modules) {
    return permissionSectionsFor(modules).flatMap((section) => section.permissions.map((entry) => entry.key));
}
export const PERMISSION_LABELS = Object.fromEntries(PERMISSION_SECTIONS.flatMap((section) => section.permissions.map((entry) => [entry.key, `${section.title}: ${entry.label}`])));
const ALL_READ = [
    'masterdata:read',
    'sales:read',
    // Rohertrag darf jeder sehen (Nutzerentscheidung 10.09.2026); wer nicht soll,
    // bekommt am Benutzer ein "verweigern" - das schlaegt jede Gruppe.
    'sales:margin',
    'purchase:read',
    'stock:read',
    'accounting:read',
    'settings:read',
];
export const ROLE_PERMISSIONS = {
    admin: [
        ...ALL_READ,
        'masterdata:write',
        'masterdata:lock',
        'sales:write',
        'sales:post',
        'sales:cancel',
        'purchase:write',
        'purchase:post',
        'purchase:cancel',
        'stock:write',
        'accounting:write',
        'accounting:export',
        'settings:write',
        'users:manage',
        'audit:read',
    ],
    accounting: [
        ...ALL_READ,
        'masterdata:write',
        'sales:post',
        'sales:cancel',
        'purchase:write',
        'purchase:post',
        'purchase:cancel',
        'accounting:write',
        'accounting:export',
        'audit:read',
    ],
    sales: ['masterdata:read', 'masterdata:write', 'sales:read', 'sales:write', 'sales:post', 'sales:margin', 'stock:read'],
    purchasing: [
        'masterdata:read',
        'masterdata:write',
        'purchase:read',
        'purchase:write',
        'purchase:post',
        'stock:read',
        'sales:read',
        'sales:margin',
    ],
    warehouse: ['masterdata:read', 'stock:read', 'stock:write', 'sales:read', 'sales:margin', 'purchase:read'],
    viewer: ALL_READ,
};
export function hasPermission(role, permission) {
    return ROLE_PERMISSIONS[role].includes(permission);
}
export function permissionsFor(role) {
    return ROLE_PERMISSIONS[role];
}
//# sourceMappingURL=roles.js.map